<template>
    <div v-if="propertyWithReviews">
        <h4 class="mb-3">{{ reviews ? reviews.length : '' }} Reviews</h4>
        <p class="text-muted small" v-if="propertyWithReviews.averageRating">
            Average {{ propertyWithReviews.averageRating }} stars
        </p>

        <carousel v-if="reviews" :autoPlay="false" :outerControls="true" @slide="setActiveIndex">
            <card class="bg-light" :class="slideCssClass">
                <template #default>
                    <card-body>
                        <container>
                            <row>
                                <column :col="12" :lg="8" v-if="propertyWithReviews.accuracyAverageRating" class="pb-3">
                                    <small class="text-muted">
                                        Listing Accuracy:
                                    </small>
                                    <form-stars-rating :value="propertyWithReviews.accuracyAverageRating" :readonly="true" onColor="primary" class="x-small" />
                                </column>

                                <column :col="12" :lg="8" v-if="propertyWithReviews.cleanlinessAverageRating" class="pb-3">
                                    <small class="text-muted">
                                        Cleanliness:
                                    </small>
                                    <form-stars-rating :value="propertyWithReviews.cleanlinessAverageRating" :readonly="true" onColor="primary" class="x-small" />
                                </column>

                                <column :col="12" :lg="8" v-if="propertyWithReviews.valueAverageRating" class="pb-3">
                                    <small class="text-muted">
                                        Value:
                                    </small>
                                    <form-stars-rating :value="propertyWithReviews.valueAverageRating" :readonly="true" onColor="primary" class="x-small" />
                                </column>

                                <column :col="12" :lg="8" v-if="propertyWithReviews.locationAverageRating" class="pb-3">
                                    <small class="text-muted">
                                        Location:
                                    </small>
                                    <form-stars-rating :value="propertyWithReviews.locationAverageRating" :readonly="true" onColor="primary" class="x-small" />
                                </column>

                                <column :md="12" :lg="8" v-if="propertyWithReviews.communicationPropertyAverageRating" class="pb-3">
                                    <small class="text-muted">
                                        Communication with Representative:
                                    </small>
                                    <form-stars-rating :value="propertyWithReviews.communicationPropertyAverageRating" :readonly="true" onColor="primary" class="x-small" />
                                </column>

                                <column :md="12" :lg="8" v-if="propertyWithReviews.communicationMarquisAverageRating" class="pb-3">
                                    <small class="text-muted">
                                        Communication with StayMarquis:
                                    </small>
                                    <form-stars-rating :value="propertyWithReviews.communicationMarquisAverageRating" :readonly="true" onColor="primary" class="x-small" />
                                </column>
                            </row>
                        </container>
                    </card-body>
                </template>
            </card>
            <reviews-item v-for="(review, index) in reviews" :key="`property-${property.id}-review-${review.id}-${index}`" :review="review" class="px-4 px-lg-5 css-opacity-transition" :class="slideCssClass ? `${slideCssClass} ${(activeIndex == index) ? '' : 'opacity-50'}` : null" />
        </carousel>

        <p v-else>
            <em>This property doesn't have enough reviews to display ratings at this time. Please check back soon.</em>
        </p>
    </div>
    <div v-else-if="!propertyWithReviews && !error" class="text-center p-5">
        <spinner size="lg"/>
    </div>
</template>

<script type="text/javascript">
    import CommonComponents from '@/components/Common.vue';
    import ReviewsItem from '@/components/Content/Public/Properties/Reviews/Item.vue';
    import ApiObject from '@/classes/ApiObject.js';
    import ApiConfig from '@/config/api.json';

    import { UseApiStore } from '@/store/Api.js';
    import { UseUIStore } from '@/store/UI.js';

    export default {
        components: {
            ReviewsItem,
            ...CommonComponents,
        },

        setup() {
            return {
                apiStore: UseApiStore(),
                uiStore: UseUIStore(),
            };
        },

        data() {
            return {
                error: null,
                disposed: false,
                emptyResults: null,
                requestsTimer: null,

                showAmenitiesModal: false,
                amenitiesModalOpen: true,

                activeIndex: 0,
            };
        },

        created() {
            this.requestData();
        },

        beforeUnmount() {
            clearTimeout(this.requestsTimer);
            this.disposed = true;
        },

        props: {
            property: {
                type: ApiObject,
                required: true,
            },
        },

        computed: {
            slideCssClass() {
                if ((!this.uiStore.isMobile) && (!this.uiStore.isTablet)) {
                    return 'half';
                }
                
                return null;
            },

            requestUrl() {
                let url = 'properties/'+this.property.id+'/reviews';
                return url;
            },
            
            propertyWithReviews() {
                let response = this.apiStore.requests[this.requestUrl];
                
                if (!response) {
                    return null;
                }

                if ((response.valid) && (response.results) && (!response.results.error)) {
                    return response.results;
                }
                else {
                    let error = 'Could not complete request';
                    if ((response.results) && (response.results.error)) {
                        if (response.results.error.message) error = response.results.error.message;
                        else error = response.results.error;
                    }
                    this.setError(error);
                }
                
                return null;
            },
            
            reviews() {

                if (
                    (this.propertyWithReviews) &&
                    (this.propertyWithReviews.approvedRatings) &&
                    (this.propertyWithReviews.approvedRatings.length)
                ) {
                    return this.propertyWithReviews.approvedRatings;
                }

                return null;
            },
            
        },

        methods: {

            setActiveIndex(index) {
                // the first slide is the averages
                if (index > 0) {
                    this.activeIndex = index - 1;
                }
                else {
                    this.activeIndex = 0;
                }
            },

            async requestData() {
                if (this.disposed) return;
                
                this.setError(null);
                this.setEmptyResults(null);
                
                clearTimeout(this.requestsTimer);

                await this.apiStore.get(this.requestUrl);

                this.requestsTimer = setTimeout(this.requestData, ApiConfig.dashboardLongCache * 1000);
            },

            setError(error) {
                this.error = error;
            },
            
            setEmptyResults(val) {
                this.emptyResults = val;
            },
        }
    };

</script>
